import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faHome, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { verifyOTP, resendOTP } from '../services/api';
import theme from '../styles/theme';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(145deg, ${theme.colors.sidebarFrom} 0%, ${theme.colors.sidebarTo} 45%, #243044 100%);
  position: fixed;
  inset: 0;
  padding: 72px 20px 24px;
  box-sizing: border-box;
  overflow-y: auto;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 60% 50% at 20% 20%, rgba(255, 107, 107, 0.2) 0%, transparent 50%),
      radial-gradient(ellipse 50% 40% at 80% 80%, rgba(255, 142, 83, 0.14) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const HomeLink = styled(Link)`
  position: fixed;
  top: 20px;
  left: 20px;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  padding: 10px 18px;
  border-radius: ${theme.radius.pill};
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(12px);
  transition: all ${theme.transitions.base};
  font-size: ${theme.typography.body};
  z-index: 2;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: translateY(-2px);
  }
`;

const Card = styled.div`
  background: ${theme.colors.card};
  padding: 36px 32px;
  border-radius: ${theme.radius.xl};
  box-shadow: ${theme.shadows.xl}, 0 0 0 1px rgba(255, 255, 255, 0.08);
  width: 100%;
  max-width: 420px;
  margin: auto;
  position: relative;
  z-index: 1;
  text-align: center;
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base};
  animation: slideUp 0.45s cubic-bezier(0.34, 1.2, 0.64, 1);

  @media (min-width: 1024px) {
    max-width: 460px;
    padding: 44px 40px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.xl}, ${theme.shadows.glowSoft};
  }
`;

const IconBadge = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${theme.radius.lg};
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: ${theme.colors.primary};
  font-size: 22px;
`;

const Title = styled.h1`
  font-size: ${theme.typography.h1};
  font-weight: 800;
  margin: 0 0 8px;
  text-align: center;
  background: ${theme.colors.accentGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
  letter-spacing: -0.04em;

  @media (min-width: 1024px) {
    font-size: 2rem;
    margin-bottom: 12px;
  }
`;

const Subtitle = styled.p`
  color: ${theme.colors.textMuted};
  margin: 0 0 28px;
  font-size: ${theme.typography.body};
  line-height: 1.6;
`;

const EmailHighlight = styled.strong`
  color: ${theme.colors.textSecondary};
  font-weight: 700;
  word-break: break-all;
`;

const OTPContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 24px;

  @media (max-width: 400px) {
    gap: 8px;
  }
`;

const OTPInput = styled.input`
  width: 46px;
  height: 52px;
  border: 1.5px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  text-align: center;
  font-size: 1.25rem;
  font-weight: 700;
  color: ${theme.colors.text};
  background: ${theme.colors.bg};
  transition: all ${theme.transitions.base};
  box-sizing: border-box;

  @media (max-width: 400px) {
    width: 42px;
    height: 48px;
    font-size: 1.1rem;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
  }

  &:focus {
    border-color: ${theme.colors.primary};
    outline: none;
    background: ${theme.colors.card};
    box-shadow: 0 0 0 4px rgba(255, 107, 107, 0.12);
  }
`;

const Button = styled.button<{ $secondary?: boolean }>`
  background: ${props =>
    props.$secondary ? 'transparent' : theme.colors.accentGradient};
  color: ${props => (props.$secondary ? theme.colors.primary : 'white')};
  border: ${props =>
    props.$secondary ? `1.5px solid ${theme.colors.cardBorder}` : 'none'};
  padding: 12px 24px;
  border-radius: ${theme.radius.md};
  font-weight: 700;
  font-size: ${theme.typography.body};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  width: 100%;
  margin-bottom: 10px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: -0.01em;
  box-shadow: ${props => (props.$secondary ? theme.shadows.xs : theme.shadows.glowSoft)};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${props =>
      props.$secondary ? theme.shadows.sm : theme.shadows.glow};
    ${props =>
      props.$secondary &&
      `
      border-color: rgba(255, 107, 107, 0.35);
      background: rgba(255, 107, 107, 0.06);
    `}
  }
`;

const Timer = styled.div`
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.small};
  margin-top: 8px;
  font-weight: 500;
`;

const LoadingIcon = styled(FontAwesomeIcon)`
  animation: spin 1s linear infinite;
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

interface LocationState {
  userId: string;
  email: string;
}

const OTPVerification: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, email } = location.state as LocationState;

  const [otp, setOTP] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [timer, setTimer] = useState(30);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!userId || !email) {
      navigate('/register');
      return;
    }
    startResendTimer();
  }, [userId, email, navigate]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timer > 0 && resendDisabled) {
      interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setResendDisabled(false);
    }
    return () => clearInterval(interval);
  }, [timer, resendDisabled]);

  const startResendTimer = () => {
    setResendDisabled(true);
    setTimer(30);
  };

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    if (value.length > 1) return;

    const newOTP = [...otp];
    newOTP[index] = value;
    setOTP(newOTP);

    if (value !== '' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      toast.error('Please enter complete OTP');
      return;
    }

    setLoading(true);
    try {
      await verifyOTP({
        userId,
        email,
        otp: parseInt(otpString, 10),
      });
      toast.success('Email verified successfully!');
      navigate('/login');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await resendOTP({ userId, email });
      toast.success('New OTP has been sent to your email');
      startResendTimer();
      setOTP(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to resend OTP');
    }
  };

  const setRef = (index: number) => (el: HTMLInputElement | null) => {
    inputRefs.current[index] = el;
  };

  const handlePaste = (e: React.ClipboardEvent, currentIndex: number) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    if (!/^\d+$/.test(pastedData)) {
      toast.error('Please paste numbers only');
      return;
    }

    const digits = pastedData.slice(0, 6).split('');
    const newOTP = [...otp];
    digits.forEach((digit, index) => {
      if (currentIndex + index < 6) {
        newOTP[currentIndex + index] = digit;
      }
    });

    setOTP(newOTP);

    const nextEmptyIndex = newOTP.findIndex(
      (digit, index) => index >= currentIndex && digit === ''
    );

    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus();
    } else {
      inputRefs.current[5]?.focus();
    }
  };

  return (
    <Container>
      <HomeLink to="/register">
        <FontAwesomeIcon icon={faHome} />
        Back
      </HomeLink>

      <Card>
        <IconBadge>
          <FontAwesomeIcon icon={faEnvelope} />
        </IconBadge>

        <Title>Verify Your Email</Title>
        <Subtitle>
          We&apos;ve sent a 6-digit verification code to
          <br />
          <EmailHighlight>{email}</EmailHighlight>
        </Subtitle>

        <OTPContainer>
          {otp.map((digit, index) => (
            <OTPInput
              key={index}
              ref={setRef(index)}
              type="number"
              inputMode="numeric"
              pattern="\d*"
              maxLength={1}
              value={digit}
              onChange={e => handleChange(index, e.target.value)}
              onKeyDown={e => handleKeyDown(index, e)}
              onPaste={e => handlePaste(e, index)}
              autoFocus={index === 0}
            />
          ))}
        </OTPContainer>

        <Button onClick={handleVerify} disabled={loading || otp.join('').length !== 6}>
          {loading ? <LoadingIcon icon={faSpinner} /> : 'Verify Email'}
        </Button>

        <Button $secondary onClick={handleResend} disabled={resendDisabled}>
          Resend OTP
        </Button>

        {resendDisabled && <Timer>Resend available in {timer} seconds</Timer>}
      </Card>
    </Container>
  );
};

export default OTPVerification;
